/**
 * UpSurv 2.0 — the office side of the wire.
 *
 * The surveyor's client is `ApiClient` in http-transport.ts. This is the OTHER
 * principal: the four `ADMIN_TOKEN` routes in functions/api/[[route]].ts that
 * Chip and Tim reach from the admin page. It is a separate class rather than
 * more methods on `ApiClient` for one reason that is not stylistic — the two
 * hold different credentials, and a single client would make it possible to
 * send the office key on a surveyor route or the surveyor's token on an admin
 * route by editing one line.
 *
 * ── What is deliberately absent ──────────────────────────────────────────────
 * There is no token in this file. D-005: minting is backend-only, Chip and Tim
 * work in NAMES. `POST /api/assignments` answers with the survey, the surveyor's
 * name and a REDACTED phone; the freshly minted link exists only inside the SMS
 * body. Nothing here can display, copy or accept a surveyor token because
 * nothing here ever holds one.
 *
 * The `key` here is the office's own shared secret (`ADMIN_TOKEN`), a different
 * credential belonging to a different principal. It is never rendered back to
 * the screen either — see officeKey.ts.
 *
 * ── Online only, and honest about it ─────────────────────────────────────────
 * Every method here is a live request. The admin surface has no offline mode
 * and must not pretend to: a roster served from a stale cache would let the
 * office assign work to a surveyor who was deactivated an hour ago. `offline`
 * is therefore a first-class error code the screen renders as a banner rather
 * than something swallowed into a generic failure.
 */

/* ────────────────────────────── wire shapes ────────────────────────────── */

/**
 * A row of `GET /api/surveyors` — the registry record, mirroring
 * `SurveyorRecord` in src/db/queries.ts.
 *
 * Restated here rather than imported so no UI module ends up depending on a
 * file that imports `@cloudflare/workers-types`. There is deliberately no token
 * field: the API never serves one, and a field on this interface would be an
 * invitation to render it.
 */
export interface SurveyorSummary {
  id: string;
  name: string;
  /** E.164, as the office entered it. Absent means "cannot be assigned work". */
  phone?: string;
  active: boolean;
  createdAt: string;
  /** When they last opened the app — written by `GET /api/surveys`. */
  lastSeenAt?: string;
}

/**
 * A row of `GET /api/admin/surveys` — every survey, across surveyors.
 *
 * Mirrors `SurveyRecord` in src/db/queries.ts, restated here for the same
 * reason `SurveyorSummary` is: no UI module may depend on a file that imports
 * `@cloudflare/workers-types`.
 */
export interface OfficeSurveySummary {
  id: string;
  origin: 'assigned' | 'field_initiated';
  status: 'assigned' | 'in_progress' | 'submitted_clean' | 'submitted_with_gaps';
  assignedTo?: string;
  buildingName?: string;
  address?: string;
  /** The address in four parts (D-039); the row's subtitle composes them with `formatAddress`. */
  city?: string;
  state?: string;
  zip?: string;
  headRevision: number;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  /** Set when the record was withdrawn — by the surveyor, or from this page. */
  discardedAt?: string;
  discardedBy?: string;
  discardedReason?: 'surveyor_discarded' | 'admin_deleted';
  /**
   * Set when the office last sent a submitted survey back to the surveyor
   * (D-018). Present alongside a non-submitted status means it is out being
   * worked again; present alongside a submitted one means it has been round the
   * loop and come back in.
   */
  recalledAt?: string;
  recalledBy?: string;
  /**
   * The phone said "sealed here, photos still to come" (D-052 §3). Beside a
   * NON-submitted status it means the surveyor is finished and the pictures
   * are arriving; beside a submitted one it is history and the row ignores it.
   */
  sealedOnDeviceAt?: string;
  photosOwed?: number;
}

/** What `POST /api/assignments` says once the work is on someone's phone. */
export interface AssignmentOutcome {
  surveyId: string;
  surveyorName: string;
  /**
   * Twilio accepted the text. False means the assignment IS saved but the
   * surveyor has not been told — the one failure the office must see (D-005).
   */
  smsAccepted: boolean;
  /**
   * Present only when a text was ATTEMPTED and did not go. Absent on a
   * suppression — nothing was attempted, so nothing failed. Never contains the
   * link.
   */
  smsError?: string;
  /**
   * No text was even attempted because outbound SMS is parked (D-010). This is
   * NOT a failure and must not be reported as one: the surveyor's app pulls the
   * job. Kept separate from `smsAccepted: false` precisely so the screen can
   * tell "Twilio refused it" apart from "there is no texting yet".
   */
  smsSuppressed?: boolean;
  /**
   * Does the surveyor hold a link that works? False alongside `smsSuppressed`
   * means the job reached them on no channel at all.
   */
  surveyorLinked?: boolean;
}

export interface AssignmentInput {
  surveyId: string;
  surveyorId: string;
  buildingName?: string;
  address?: string;
  /** City / State / ZIP from the assign form (D-039); the phone seeds them as B2a–c. */
  city?: string;
  state?: string;
  zip?: string;
  createdBy?: string;
}

/**
 * A freshly printed onboarding code (D-013).
 *
 * `url` carries a live single-use secret and is the ONE exception to the "no
 * credential on the admin surface" rule at the top of this file — because it is
 * not a surveyor token, it is a 24-hour ticket that is exchanged for one, and
 * because it is rendered exclusively as a QR image. `AdminScreen` never puts it
 * in a text node, never in an input, never on the clipboard. If a future change
 * displays this string, that change has misread D-005 and D-013 together.
 */
export interface OnboardingLinkIssued {
  id: string;
  surveyorId: string;
  surveyorName: string;
  /** Encoded into the QR and nowhere else. */
  url: string;
  /**
   * The same link as nine typeable letters, grouped `BCD-FGH-JKL` (D-019).
   *
   * The ONE credential-shaped string this app renders as text, and only
   * because a code nobody can read out cannot rescue a camera that will not
   * focus. Not a token: it is a single-use, 24-hour ticket exchanged for one,
   * and it dies the moment it is used or a new code is printed.
   */
  pairCode: string;
  createdAt: string;
  expiresAt: string;
  ttlHours: number;
  /** How many still-open codes this one killed. Usually 0, sometimes 1. */
  invalidatedPriorLinks: number;
}

/* ─────────────────────────────── the error ─────────────────────────────── */

/**
 * Everything this client rejects with, so the screen has exactly one shape to
 * branch on. `code` is the API's own `error` string where there was one, plus
 * two the transport invents: `offline` (the request never left) and
 * `bad_response` (something that was not JSON came back).
 */
export class AdminError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, options: { status: number; code: string; cause?: unknown }) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'AdminError';
    this.status = options.status;
    this.code = options.code;
  }
}

export interface AdminClientOptions {
  baseUrl?: string;
  /** Read fresh per call, so signing out takes effect without a reload. */
  key: () => string | null;
  fetchImpl?: typeof fetch;
}

/* ─────────────────────────────── the client ────────────────────────────── */

export class AdminClient {
  private readonly baseUrl: string;
  private readonly key: () => string | null;
  private readonly fetchImpl: typeof fetch;

  constructor(options: AdminClientOptions) {
    this.baseUrl = (options.baseUrl ?? '/api').replace(/\/$/, '');
    this.key = options.key;
    this.fetchImpl = options.fetchImpl ?? ((...args) => globalThis.fetch(...args));
  }

  private async request(path: string, init: RequestInit = {}): Promise<Response> {
    const key = this.key();
    if (!key) {
      throw new AdminError('Enter the office key to manage surveyors.', {
        status: 401,
        code: 'no_office_key',
      });
    }
    const headers = new Headers(init.headers);
    headers.set('authorization', `Bearer ${key}`);

    try {
      return await this.fetchImpl(`${this.baseUrl}${path}`, { ...init, headers });
    } catch (cause) {
      throw new AdminError('No connection to the office server.', {
        status: 0,
        code: 'offline',
        cause,
      });
    }
  }

  private async json<T>(response: Response): Promise<T> {
    try {
      return (await response.json()) as T;
    } catch (cause) {
      throw new AdminError('The server sent something that was not JSON.', {
        status: response.status,
        code: 'bad_response',
        cause,
      });
    }
  }

  /** Turn a refusal into an `AdminError` that keeps the API's own error code. */
  private async fail(response: Response): Promise<never> {
    let code = 'http_error';
    let message = '';
    try {
      const parsed = (await response.json()) as { error?: string; message?: string };
      if (parsed.error) code = parsed.error;
      message = parsed.message ?? '';
    } catch {
      /* a body that will not parse adds nothing */
    }
    throw new AdminError(message || `Request failed (${String(response.status)}).`, {
      status: response.status,
      code,
    });
  }

  private async send<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await this.request(path, init);
    if (!response.ok) await this.fail(response);
    return this.json<T>(response);
  }

  private static jsonBody(value: unknown): RequestInit {
    return {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(value),
    };
  }

  /* ──────────────────────────── the registry ───────────────────────────── */

  /**
   * The roster. Inactive surveyors are included by default: the office needs to
   * SEE that Dave is deactivated, otherwise "why is Dave not in the list" is
   * indistinguishable from "Dave was never added".
   */
  async listSurveyors(options: { includeInactive?: boolean } = {}): Promise<SurveyorSummary[]> {
    const includeInactive = options.includeInactive ?? true;
    const body = await this.send<{ surveyors: SurveyorSummary[] }>(
      `/surveyors?includeInactive=${includeInactive ? 'true' : 'false'}`,
    );
    return body.surveyors;
  }

  /** Add a surveyor. Name and phone; the server mints the id and the seed hash. */
  async createSurveyor(input: { name: string; phone?: string }): Promise<SurveyorSummary> {
    const body = await this.send<{ surveyor: SurveyorSummary }>(
      '/surveyors',
      AdminClient.jsonBody({ name: input.name, ...(input.phone ? { phone: input.phone } : {}) }),
    );
    return body.surveyor;
  }

  /** Deactivate. Their token stops authenticating on the very next request. */
  async deactivateSurveyor(id: string): Promise<SurveyorSummary> {
    const body = await this.send<{ surveyor: SurveyorSummary }>(
      `/surveyors/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
    return body.surveyor;
  }

  /**
   * Undo a deactivation. Their OLD link stays dead — see the route's comment;
   * the next assignment is what texts them a working one.
   */
  async reactivateSurveyor(id: string): Promise<SurveyorSummary> {
    const body = await this.send<{ surveyor: SurveyorSummary }>(
      `/surveyors/${encodeURIComponent(id)}/reactivate`,
      { method: 'POST' },
    );
    return body.surveyor;
  }

  /**
   * Print a one-time onboarding code for a surveyor (D-013).
   *
   * Every call mints a NEW code and kills any the surveyor still had open — the
   * response says how many. That is what makes "show it again" safe: the office
   * cannot know where the last code ended up (a photographed screen, a
   * scroll-back, a second monitor), so a replacement has to be a replacement
   * rather than an addition.
   */
  async issueOnboardingLink(surveyorId: string): Promise<OnboardingLinkIssued> {
    const body = await this.send<{ link: OnboardingLinkIssued }>(
      '/onboarding/links',
      AdminClient.jsonBody({ surveyorId }),
    );
    return body.link;
  }

  /* ────────────────────────── survey records ───────────────────────────── */

  /**
   * Every survey the office holds, newest first. Withdrawn ones are included
   * and labelled — "it was removed" and "it was never created" are different
   * facts, and this is the only screen entitled to tell them apart.
   */
  async listSurveys(options: { includeDiscarded?: boolean } = {}): Promise<OfficeSurveySummary[]> {
    const includeDiscarded = options.includeDiscarded ?? true;
    const body = await this.send<{ surveys: OfficeSurveySummary[] }>(
      `/admin/surveys?includeDiscarded=${includeDiscarded ? 'true' : 'false'}`,
    );
    return body.surveys;
  }

  /**
   * Remove a survey from the office's records — the ONLY route that can remove
   * a submitted one (D-012).
   *
   * It is a mark, not an erasure: the submission and every revision behind it
   * survive, because they are what an estimate was built from. What changes is
   * that the record stops appearing in any list. There is no undo.
   */
  async deleteSurvey(surveyId: string): Promise<OfficeSurveySummary | null> {
    const body = await this.send<{ survey: OfficeSurveySummary | null }>(
      `/surveys/${encodeURIComponent(surveyId)}`,
      { method: 'DELETE' },
    );
    return body.survey;
  }

  /**
   * RECALL — send a submitted survey back to the surveyor (D-018).
   *
   * The office's counterpart to "Remove": one takes a finished job out of the
   * records, the other puts it back in the surveyor's hands. Spec §2.3 puts both
   * here and nowhere else — the surveyor cannot reopen their own submission any
   * more than they can clear their own gap.
   *
   * Nothing is rewritten by it. The submitted revision stays in the record's
   * history exactly as it arrived; the recall appends a revision that reopens
   * the survey and records who did it and when. The surveyor's device picks it
   * up on its ordinary pull.
   */
  async recallSurvey(surveyId: string): Promise<OfficeSurveySummary | null> {
    const body = await this.send<{ survey: OfficeSurveySummary | null }>(
      `/surveys/${encodeURIComponent(surveyId)}/recall`,
      { method: 'POST' },
    );
    return body.survey;
  }

  /* ──────────────────────────── assignment ─────────────────────────────── */

  /**
   * Assign a survey and text the surveyor their link.
   *
   * 502 is NOT treated as a failure here, and that is the important line in this
   * file. The API returns 502 when Twilio refused the text — but the survey was
   * already written and assigned before the send was attempted. Throwing would
   * tell the office "that didn't work" about an assignment that exists, and they
   * would do it again. Instead it resolves with `smsAccepted: false` so the
   * screen can say the true thing: it is assigned, and the text did not go.
   */
  async assign(input: AssignmentInput): Promise<AssignmentOutcome> {
    const response = await this.request('/assignments', AdminClient.jsonBody(input));

    if (!response.ok && response.status !== 502) await this.fail(response);

    const body = await this.json<{
      survey?: { id?: string };
      surveyor?: { name?: string; linked?: boolean };
      sms?: { accepted?: boolean; error?: string; suppressed?: boolean };
    }>(response);

    const accepted = body.sms?.accepted === true;
    const suppressed = body.sms?.suppressed === true;
    return {
      surveyId: body.survey?.id ?? input.surveyId,
      surveyorName: body.surveyor?.name ?? input.surveyorId,
      smsAccepted: accepted,
      ...(suppressed ? { smsSuppressed: true } : {}),
      ...(typeof body.surveyor?.linked === 'boolean'
        ? { surveyorLinked: body.surveyor.linked }
        : {}),
      // Suppression is not an error and must not arrive dressed as one. A
      // consumer that renders `smsError` whenever it is present would otherwise
      // put "outbound SMS is parked" on screen under a red heading, which is the
      // same wrong story D-010 removed from `assignmentMessage`.
      ...(accepted || suppressed
        ? {}
        : { smsError: body.sms?.error ?? 'The text could not be sent.' }),
    };
  }
}
